import { WORDPRESS_SERVICE_URL } from '~/modules/config';
import { handleException } from '~/modules/utilities/error_utilities';
import { http } from '~/modules/utilities/http';

export type WordPressContentType = {
    name: string;
    slug: string;
    count: number;
    labels: {
        singular: string;
        plural: string;
    };
};

export type WordPressPersona = {
    name: string;
    slug: string;
    count: number;
    labels: {
        singular: string;
        plural: string;
    };
};

export type WordPressPostData = {
    id: number;
    date: string;
    slug: string;
    status: string;
    content: {
        rendered: string;
    };
    title: {
        rendered: string;
    };
    excerpt: {
        rendered?: string;
    };
    featured_media_url?: string;
    link: string;
    authors?: [
        {
            id: number;
            display_name: string;
            user_nicename: string;
            avatar: string;
        },
    ];
    read_time: string;
    content_type: WordPressContentType;
    yoast_head_json?: {
        title: string;
        description: string;
        author: string;
        og_locale: string;
        og_type: string;
        og_title: string;
        og_url: string;
        og_site_name: string;
        og_image: [
            {
                width: number;
                height: number;
                url: string;
                type: string;
            },
        ];
        article_published_time: string;
        article_modified_time: string;
        twitter_card: string;
        twitter_creator: string;
        twitter_site: string;
        twitter_misc: Record<string, string>;
    };
};

type WordPressFilteredResponse = {
    posts: WordPressPostData[];
    totalItems: number;
};

export type WordPressQueryType = 'page' | 'collection' | 'post';
export type GetPostContentOptions = {
    previewToken?: string;
    language?: string;
    wordPressQueryType?: WordPressQueryType;
};

export class WordPressService {
    async getPostContent(
        slug: string,
        options?: GetPostContentOptions,
        section?: string,
    ): Promise<WordPressPostData | undefined> {
        let url = WORDPRESS_SERVICE_URL;

        const subRoute: Record<WordPressQueryType, string> = {
            collection: 'collections',
            page: 'pages',
            post: 'posts',
        };
        let routeType: WordPressQueryType = 'post';
        if (slug === 'home') {
            routeType = 'page';
        } else if (options?.wordPressQueryType) {
            routeType = options?.wordPressQueryType;
        }

        const subRoutePath = subRoute[routeType];

        url += `/wp-json/ax/v1/${subRoutePath}?slug=${slug}`;
        if (options?.previewToken) {
            url += `&axwp-preview-id=${options.previewToken}`;
        }
        if (options?.language) {
            url += `&language=${options.language}`;
        }
        if (section) {
            url += `&section=${section}`;
        }

        try {
            const response = await http.get(url, {
                includeCorrelationIdHeader: false,
            });

            if (Array.isArray(response.data)) {
                const contentArray = response.data as WordPressPostData[];
                if (contentArray.length > 0) {
                    return contentArray[0];
                }
                return undefined;
            }
            return response.data as WordPressPostData;
        } catch (error) {
            handleException(error, 'WordPressService.getPostContent');
            throw error;
        }
    }

    async getContentTypes(language?: string): Promise<WordPressContentType[]> {
        let url = `${WORDPRESS_SERVICE_URL}/wp-json/ax/v1/content-type?hide_empty=true`;
        if (language) {
            url += `&language=${language}`;
        }

        try {
            const response = await http.get(url, {
                includeCorrelationIdHeader: false,
            });

            // This trims out properties we don't care about
            return response.data.map((contentType: WordPressContentType) => ({
                slug: contentType.slug,
                name: contentType.name,
                count: contentType.count,
                labels: contentType.labels,
            }));
        } catch (error) {
            handleException(error, 'WordPressService.getContentTypes');
            throw error;
        }
    }

    async getPersonas(language?: string): Promise<WordPressPersona[]> {
        let url = `${WORDPRESS_SERVICE_URL}/wp-json/ax/v1/persona?hide_empty=true`;
        if (language) {
            url += `&language=${language}`;
        }

        try {
            const response = await http.get(url, {
                includeCorrelationIdHeader: false,
            });

            // This trims out properties we don't care about
            return response.data.map((persona: WordPressPersona) => ({
                slug: persona.slug,
                name: persona.name,
                count: persona.count,
                labels: persona.labels,
            }));
        } catch (error) {
            handleException(error, 'WordPressService.getPersonas');
            throw error;
        }
    }

    async getFilteredPosts(
        page: number,
        pageSize: number,
        contentType?: string,
        persona?: string,
        language?: string,
        learnLanding?: boolean,
    ): Promise<WordPressFilteredResponse> {
        if (!contentType && !persona) {
            throw new Error('getFilteredPosts requires at least one filter to be passed.');
        }

        try {
            let url = `${WORDPRESS_SERVICE_URL}/wp-json/ax/v1/posts?page=${page}&per_page=${pageSize}`;

            if (contentType) {
                url += `&content-type_slug=${contentType}`;
            }
            if (persona) {
                url += `&persona_slug=${persona}`;
            }
            if (language) {
                url += `&language=${language}`;
            }
            if (learnLanding) {
                url += `&learn_landing=${learnLanding}`;
            }

            const response = await http.get(url, {
                includeCorrelationIdHeader: false,
            });

            return {
                posts: response.data as WordPressPostData[],
                totalItems: Number(response.headers['x-wp-total']),
            };
        } catch (error) {
            handleException(error, 'WordPressService.getFilteredPosts');
            throw error;
        }
    }
}
